import React, { useEffect, useState } from "react";
import "./ChatbotComponent.css";
import config from "../config";

const ChatbotComponent = ({ user }) => {
  const userId = user || null;

  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const baseUrl = config.backend_Url.endsWith('/') ? config.backend_Url : `${config.backend_Url}/`;

  const API_URL = `${baseUrl}xyassistant/chat`;

  // API CALL
  const callAPI = async (payload) => {
    let url = API_URL;
    if (userId) url += `?user_id=${userId}`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res
          .json()
          .catch(() => ({ message: res.statusText }));
        throw new Error(
          errorData.message || `HTTP error! status: ${res.status}`,
        );
      }

      return await res.json();
    } catch (error) {
      throw error;
    }
  };

  // ADD MESSAGE
  const addMessage = (text, sender = "bot") => {
    setMessages((prev) => [...prev, { text, sender }]);
  };

  // INITIAL LOAD
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initChat();
    }
  }, [isOpen]);

  const initChat = async () => {
    setLoading(true);
    try {
      const data = await callAPI({ action: "main" });

      if (data.message) addMessage(data.message);
      if (data.options) setOptions(data.options);
    } catch (error) {
      addMessage(
        `Failed to initialize chat: ${error.message || "Network error"}`,
      );
    } finally {
      setLoading(false);
    }
  };

  // HANDLE CLICK
  const handleClick = async (option) => {
    addMessage(option.label, "user");
    setOptions([]);
    setLoading(true);

    try {
      const data = await callAPI({ action: option.value });

      if (data.message) addMessage(data.message);

      if (data.data) {
        data.data.forEach((item) => {
          if (item.invoice_number) {
            addMessage({ type: "invoice", data: item });
          } else {
            addMessage({ type: "payment", data: item });
          }
        });
      }

      if (data.redirect_url) {
        window.open(data.redirect_url);
      }

      if (data.options) {
        setOptions(data.options);
      }
    } catch (error) {
      addMessage(`⚠️ API failed: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleChat = () => {
    if (isOpen) {
      // closing → reset
      setMessages([]);
      setOptions([]);
    }

    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* FLOATING BUTTON */}
      <div className="chatbot-floating-btn" onClick={toggleChat}>
        {isOpen ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          "💬"
        )}
      </div>

      {/* CHAT WINDOW */}
      {isOpen && (
        <div className="chatbot-container">
          {/* HEADER */}
          <div className="chatbot-header">Xynex Support Bot 🤖</div>

          {/* CHAT AREA */}
          <div className="chatbot-chat-area">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`chatbot-message ${
                  msg.sender === "user" ? "chatbot-user" : "chatbot-bot"
                }`}
              >
                {typeof msg.text === "string" ? (
                  msg.text
                ) : msg.text.type === "invoice" ? (
                  <div className="chatbot-card">
                    <div className="chatbot-invoice-header">🧾 Invoice</div>

                    <div>
                      <b>Invoice ID:</b> {msg.text.data.invoice_number}
                    </div>
                    <div>
                      💰 Amount: <b>₹{msg.text.data.amount}</b>
                    </div>
                    <div>
                      📅 {new Date(msg.text.data.date).toLocaleString()}
                    </div>

                    <div className="chatbot-paid-tag">PAID</div>
                  </div>
                ) : (
                  <div className="chatbot-card">
                    <div>
                      💰 <b>₹{msg.text.data.amount}</b>
                    </div>
                    <div>
                      📅 {new Date(msg.text.data.date).toLocaleString()}
                    </div>
                    <div>
                      {msg.text.data.status === "PAID" ? "✅" : "⏳"}{" "}
                      {msg.text.data.status}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {loading && <div className="chatbot-loading">Typing...</div>}
          </div>

          {/* OPTIONS */}
          <div className="chatbot-options">
            {options.map((opt, i) => (
              <button
                key={i}
                className="chatbot-button"
                onClick={() => handleClick(opt)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotComponent;